import { exposeAccessors, exposeFunctions, exposeValues } from './globals';
import { S, pageHandle, subscribe } from './state';
import * as A from './actions';
import * as Boot from './boot';
import * as Writes from './mapWrites';
import * as Presence from './presence';
import { PIPELINE_DISPLAY } from '../lib/docs';
import { isExemptPlan } from '../lib/health';
import { hexA, shade, toHex6 } from '../lib/color';
import { escapeHtml, isHttpUrl, workDayKey } from '../lib/format';
import { helpText } from '../i18n/help';
import { requestTopbarFit } from '../features/topbar/topbarFit';
import type { PresenceUser } from '../features/presence/PresenceStack';

/**
 * WHAT THE PAGE PUBLISHES, and why each name is on the list.
 *
 * `app/globals.ts` explains the mechanism and the rule; this is the roster. Every entry is here
 * because `e2e/` reads it, calls it or assigns to it inside `page.evaluate`, and those specs are
 * the migration's acceptance gate — they may not be edited, so the app has to keep answering to
 * the names they use. A classic script gave them that by accident; a module has to mean it.
 *
 * NOTHING HERE IS A COPY. `nodes` is the map the app renders from, `openDoc` is the function the
 * pills call, `applyPermissions` is the one boot calls. A spec that sets `nodes[id].collapsed`
 * and calls `render()` is doing exactly what a click does.
 *
 * ONE ENTRY IS NOT DRIVEN BY A SPEC: `InfoStationReact`, which other CONIGON apps and a couple
 * of embedded pages have used to ask this app to open something. It is the same handle it has
 * always been, narrowed to what was actually reachable.
 *
 * WHAT IS DELIBERATELY ABSENT is anything the suite does not use. This is not "the app's API";
 * a name that nothing asks for should be imported, not published.
 */
export function publishPageScope(): void {
  /* ---- state a spec READS ------------------------------------------------------------ */
  exposeAccessors({
    // The map itself. Read constantly, and MUTATED in place by several specs to reach a case
    // the real data does not contain (a collapsed branch, a renamed node, an attached doc).
    nodes: { get: () => S.nodes },
    edges: { get: () => S.edges },
    tags: { get: () => S.tags },
    tagEdges: { get: () => S.tagEdges },
    links: { get: () => S.links },
    docTypes: { get: () => S.docTypes },
    docUrls: { get: () => S.docUrls },
    docTitles: { get: () => S.docTitles },
    // The permissions payload. Specs mutate `perm.roles` and call applyPermissions() to check
    // that losing a role really closes a door rather than only hiding the button.
    perm: { get: () => S.perm },
    // ASSIGNED by 22-topbar-left, 25-logs and 27-aidoc-console, which withdraw one AI
    // capability to prove the surface goes ABSENT rather than present-and-erroring.
    caps: { get: () => S.caps, set: (v) => { S.caps = v; } },

    editing: { get: () => S.editing },
    linkArmed: { get: () => S.linkArmed },
    showLinks: { get: () => S.showLinks },
    aiDocMode: { get: () => S.aiDocMode },
    currentLocale: { get: () => S.currentLocale },
    searchQuery: { get: () => S.searchQuery },
    searchScope: { get: () => S.searchScope },
    // READ by 23-topbar-search: the doc-kind narrowing must be CLEARED when the permission
    // behind it is withdrawn, and only the state itself can prove that.
    searchDocKind: { get: () => S.searchDocKind },
    activeTags: { get: () => S.activeTags },
    activeDocStatus: { get: () => S.activeDocStatus },
    forceOpen: { get: () => S.forceOpen },
    chatHighlight: { get: () => S.chatHighlight },
    hiddenNodeSet: { get: () => S.hiddenNodeSet },
    hiddenTagSet: { get: () => S.hiddenTagSet },
    popupFor: { get: () => S.popupFor },
    aiDocCfgCache: { get: () => S.aiDocCfgCache },

    // The doc view. `docMode` is no longer STORED (V0.53.5 — ONE edit mode): reading it asks
    // whether the editor is on screen (`docEditorActive`), which is what 34 and 39 assert, and
    // assigning it enters or leaves edit mode — the real transition, which is what
    // 32-doc-log-timeline needs when it sets 'edit' with nothing open. Neither is a shadow
    // variable: both are the state and the function the app itself uses.
    docCurrent: { get: () => S.docCurrent },
    docCurrentId: { get: () => S.docCurrentId },
    docMode: {
      get: () => (A.docEditorActive() ? 'edit' : 'view'),
      set: (v) => A.setEditing(v === 'edit'),
    },

    // ASSIGNED by 21-topbar-right, which injects a nine-person roster to reach the "+N"
    // collapse — a case the real map cannot be made to produce on demand.
    presenceRoster: {
      get: () => Presence.getRoster(),
      set: (v: readonly PresenceUser[]) => Presence.setRoster(v),
    },
  });

  exposeValues({
    // The seven pipeline stages, as the console's tabs and the map's filter use them —
    // the real table, under its real name (the V0.38 spec-alignment commit renamed the
    // published global from the retired STATUS_DISPLAY together with the d.ts and every
    // spec that reads it, as the contract requires of a published-name change).
    PIPELINE_DISPLAY,
    // The SAME Set the flush drains, so what 27-aidoc-console reads is what is actually owed.
    dirtyAiFields: Writes.dirtyAiFields,
  });

  /* ---- what a spec CALLS ------------------------------------------------------------- */
  exposeFunctions({
    // "The state moved, show it" — the two names ~50 call sites already used, and what a spec
    // says after mutating a node object directly.
    render: A.render,
    drawWires: A.drawWires,

    // The permission and capability gates, which several specs use to skip themselves cleanly
    // when the account they run as cannot reach a surface.
    // `canEdit` is the contract NAME (e2e/helpers/page-globals.d.ts); what it has meant since
    // V0.37 is the edit-MODE gate, which is `canEnterEdit` — the real function, not a shim.
    canEdit: A.canEnterEdit,
    canUserPerms: A.canUserPerms,
    canAiAdmin: A.canAiAdmin,
    canLiveVerify: A.canLiveVerify,
    canChat: A.canChat,
    canDocBtn: A.canDocBtn,
    permHidden: A.permHidden,
    tagHidden: A.tagHidden,
    isHidden: A.isHidden,
    refreshHiddenSets: A.refreshHiddenSets,
    applyPermissions: A.applyPermissions,
    setEditing: A.setEditing,

    // The graph and the tag tree.
    childrenOf: A.childrenOf,
    descendants: A.descendants,
    tagChildren: A.tagChildren,
    tagDescendants: A.tagDescendants,
    tagCount: A.tagCount,
    isTagAncestor: A.isTagAncestor,

    // Documents.
    docTypeOf: A.docTypeOf,
    docUrlFor: A.docUrlFor,
    // The successor of the retired effectiveDocStatus derivation, under its own name (renamed
    // on both sides at once by the V0.38 spec-alignment commit; frozen 15's assertion on the
    // old derivation was deleted separately, per the delete-don't-weaken rule).
    pipelineStageOf: A.pipelineStageOf,
    // The exemption rule the AI console applies to its counts (`AiTab.tsx`), so a spec that
    // recomputes those counts applies the same one rather than a copy of it.
    isExemptPlan,
    firstVisibleDocOf: A.firstVisibleDocOf,
    openDoc: A.openDoc,
    openDocEditor: A.openDocEditor,
    openDocInCurrentMode: A.openDocInCurrentMode,
    navOpenNode: A.navOpenNode,
    closeDoc: A.closeDoc,
    openDocPicker: A.openDocPicker,
    closeDocPickModal: A.closeDocPickModal,
    openLogDetail: A.openLogDetail,

    // Search.
    nodeMatchesSearch: A.nodeMatchesSearch,
    computeForceOpen: A.computeForceOpen,
    setSearchScope: A.setSearchScope,
    setSearchDocKind: A.setSearchDocKind,

    // Screens, opened by name.
    openPopup: A.openPopup,
    openPermPage: A.openPermPage,
    openLogs: A.openLogs,
    canViewLogs: A.canViewLogs,
    // The dashboard by name, which the Logs tab needs: it is the one tab reachable WITHOUT
    // edit mode, so a spec cannot get there by clicking the edit-toolbar button.
    openDashboard: A.openDashboard,
    closeDashboard: A.closeDashboard,
    openTargets: A.openTargets,
    openAiDocModal: A.openAiDocModal,
    openAiDocConfig: A.openAiDocConfig,
    startLiveVerify: A.startLiveVerify,

    // Presence and the undo stack.
    disconnectPresence: A.disconnectPresence,
    rebuildPresence: A.rebuildPresence,
    pushUndo: A.pushUndo,

    // The language switch, which several specs drive to check a locale round-trip.
    setLocale: Boot.setLocale,

    // The top bar's re-measure. 21-topbar-right captures this and re-registers a counting
    // wrapper around it, to prove that React changing the width of a zone — "Expand all" and
    // "Collapse all" are not the same width — asks for a measure that no resize event would.
    // The name survived plan 1.3; what it points at is the overflow-aware fit controller.
    fitTopbar: requestTopbarFit,

    // Pure helpers a spec uses to build an expectation. These ARE the lifted implementations
    // in `client/src/lib/`, which is why `15-core-parity` compares against a frozen recording
    // of the originals rather than against these — see that file's header.
    hexA,
    shade,
    toHex6,
    escapeHtml,
    isHttpUrl,
    workDayKey,
  });

  /* ---- the app's external handle ----------------------------------------------------- */
  /**
   * `window.InfoStationReact` is not this migration's invention and does not go with it: the
   * surrounding suite has asked this app to open a document or re-render through it, and
   * `e2e/17-bridge-and-toast.spec.ts` and `e2e/21-topbar-right.spec.ts` drive it directly.
   *
   * TWO OF THESE NAMES MEAN SOMETHING DIFFERENT NOW, and it is worth saying which. During the
   * migration `connect` installed a state PROVIDER and `sync` announced that the provider's
   * state had moved — both pointing from the vanilla script into React. There is no other
   * implementation to point at any more, so:
   *
   *  - `sync()` is the announcement point itself (`state.ts`'s `publish`). The app calls it
   *    through this property, which is what makes "wrap sync and you see every state change"
   *    exactly true.
   *  - `connect(listener)` is the other direction now: subscribe to state changes, and get an
   *    unsubscribe back. That is what an embedder wanted from the name all along.
   *  - `registerActions(overrides)` still overrides an action by name, which is how a spec
   *    counts calls to something the app invokes internally.
   *  - `mountAll()` re-renders every region into its host.
   */
  Object.assign(pageHandle, {
    connect: subscribe,
    registerActions: (next: Partial<typeof A.actions>) => Object.assign(A.actions, next),
    helpText,
    openDoc: A.openDoc,
    openPopup: A.openPopup,
    setLocale: Boot.setLocale,
  });
  (globalThis as unknown as { InfoStationReact: unknown }).InfoStationReact = pageHandle;
}
